<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'

/**
 * AppTextarea — the multi-line text field, in AppInput's chrome so the two stack flush.
 *
 * `rows` tall at rest, it grows with its text up to `maxRows` and then scrolls. Enter breaks
 * the line, unless `submitOnEnter`: then Enter emits `submit` and Shift+Enter breaks it, as a
 * composer wants. Native attributes (id, name, maxlength, aria-*) forward to the <textarea>
 * via `v-bind="$attrs"`.
 *
 *   <AppTextarea v-model="notes" variant="form" size="md" :rows="3" placeholder="Notes" />
 */
defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue:    { type: [String, Number], default: '' },
  /**
   * 'toolbar' (default) — AppInput's compact shape: border, rounded-md.
   * 'form' — the bordered form field: rounded-lg, ring-gray-300, bg-gray-50, tint-1 focus ring.
   * 'flush' — no chrome of its own: the surround supplies it, as the Help Hub's composer box does.
   */
  variant:       { type: String, default: 'toolbar', validator: (variant) => ['toolbar', 'form', 'flush'].includes(variant) },
  size:          { type: String, default: 'sm', validator: (size) => ['sm', 'md', 'lg'].includes(size) }, // sm text-xs | md, lg text-sm — AppInput's insets, without its fixed height
  placeholder:   { type: String, default: '' },
  disabled:      { type: Boolean, default: false },
  error:         { type: Boolean, default: false },
  rows:          { type: Number, default: 3 },
  // Lines it grows to before it scrolls; null grows without a cap.
  maxRows:       { type: Number, default: 8 },
  // Enter emits `submit` instead of breaking the line; Shift+Enter still breaks it.
  submitOnEnter: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'submit', 'blur', 'focus'])

const textareaRef = ref(null)

const isForm  = computed(() => props.variant === 'form')
const isFlush = computed(() => props.variant === 'flush')

const shapeClass = computed(() => {
  if (isFlush.value) return ''
  return isForm.value ? 'rounded-lg ring-1' : 'border rounded-md'
})

// AppInput's insets per variant and size; the rows set the height.
const sizeClass = computed(() => {
  if (isFlush.value) return 'p-0'
  if (props.size === 'lg') return 'px-3 py-2.5'
  if (isForm.value) return props.size === 'md' ? 'px-4 py-2' : 'px-3 py-1.5'
  return props.size === 'md' ? 'px-2.5 py-2' : 'px-2 py-1.5'
})

const textSizeClass = computed(() => (props.size === 'sm' ? 'text-xs' : 'text-sm'))

const stateClass = computed(() => {
  if (isFlush.value) return props.disabled ? 'cursor-not-allowed' : ''
  if (isForm.value) {
    if (props.disabled) return 'bg-gray-200/80 ring-gray-300 cursor-not-allowed'
    if (props.error)    return 'bg-red-50 ring-red-500 focus-within:ring-red-500'
    return 'bg-gray-50 ring-gray-300 focus-within:ring-tint-1'
  }
  if (props.disabled) return 'bg-gray-50 border-gray-200 cursor-not-allowed'
  if (props.error)    return 'bg-white border-red-500 focus-within:border-red-500'
  return 'bg-white border-gray-200 focus-within:border-tint-1'
})

// `rows` tall at rest, growing with the text to `maxRows`, then it scrolls.
const grow = () => {
  const el = textareaRef.value
  if (!el) return
  el.style.height = 'auto'
  if (!props.maxRows) {
    el.style.height = `${el.scrollHeight}px`
    return
  }
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20
  el.style.height = `${Math.min(el.scrollHeight, lineHeight * props.maxRows)}px`
}

const onInput = (event) => {
  emit('update:modelValue', event.target.value)
  grow()
}

// A value set from outside (a sent message cleared) resizes the field too.
watch(() => props.modelValue, () => nextTick(grow))
onMounted(grow)

const onEnter = (event) => {
  if (!props.submitOnEnter || event.shiftKey) return
  event.preventDefault()
  emit('submit', event)
}

defineExpose({ focus: () => textareaRef.value?.focus() })
</script>

<template>
  <label class="flex w-full transition-colors" :class="[shapeClass, sizeClass, stateClass]">
    <!-- AppInput's resets: the field keeps no border, padding, fill or ring of its own inside the wrapping label. -->
    <textarea
      ref="textareaRef"
      v-bind="$attrs"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :rows="rows"
      class="
        min-w-0 w-full resize-none leading-normal
        border-0 p-0 bg-transparent
        appearance-none outline-none
        focus:outline-none focus:ring-0
        text-gray-700 placeholder:text-gray-400
      "
      :class="textSizeClass"
      @input="onInput"
      @keydown.enter="onEnter"
      @blur="emit('blur', $event)"
      @focus="emit('focus', $event)"
    />
  </label>
</template>
